import moment from 'moment';
import { Translate } from 'react-admin';
import { ExpiryTimeGranularityType, ExpiryTimeGranularity } from '@x-guard/xgac-types/xgac';

// Month starts from 28 days when rounded to the nearest time granularity as the number of days in a month can vary
// The exact values are used for calculating the milliseconds to the nearest time granularity, so the months are always 30 days
const expiryTimeGranularityMap = (rounded = true) => {

  return {
    day: 1000 * 60 * 60 * 24,
    week: 1000 * 60 * 60 * 24 * 7,
    month: rounded ? 1000 * 60 * 60 * 24 * 28 : 1000 * 60 * 60 * 24 * 30,
    year: 1000 * 60 * 60 * 24 * 365,
  };

};

/**
 * The general pattern for 'rounding' milliseconds to the nearest time granularity is either displaying the largest period fitting in the milliseconds,
 * or the next one after the largest fitting one.
 * For example, if a month(s) fit in the milliseconds, and the remainder is less than a week, then the month(s) is(are) displayed.
 * If the remainder is more than a week, weeks are displayed.
 * The only exception to this pattern is year, because it is easier to comprehend years in terms of months, not quarters.
 */
export const getNearestExpiryTimeGranularity = (
  milliseconds: number,
): ExpiryTimeGranularity => {

  const absMs = Math.abs(milliseconds);
  let displayPeriod: ExpiryTimeGranularityType = ExpiryTimeGranularityType.Month;
  const map = expiryTimeGranularityMap();

  if (absMs >= map.year) {

    const remaining = absMs % map.year;
    displayPeriod = remaining >= map.month
      ? ExpiryTimeGranularityType.Month
      : ExpiryTimeGranularityType.Year;

  } else if (absMs >= map.month) {

    const remaining = absMs % map.month;
    displayPeriod = remaining >= map.week
      ? ExpiryTimeGranularityType.Week
      : ExpiryTimeGranularityType.Month;

  } else if (absMs >= map.week) {

    const remaining = absMs % map.week;
    displayPeriod = remaining >= map.day
      ? ExpiryTimeGranularityType.Day
      : ExpiryTimeGranularityType.Week;

  } else {

    displayPeriod = ExpiryTimeGranularityType.Day;

  }
  return {
    type: displayPeriod,
    value: Math.floor(absMs / map[displayPeriod]),
  };

};

export const getExpiryTimeGranularity = (
  startDate: string,
  expiryDate: string,
): ExpiryTimeGranularity => {

  const ms = moment(expiryDate).startOf('day')
    .diff(moment(startDate).startOf('day'));
  return getNearestExpiryTimeGranularity(ms);

};
export const roundDaysToExpiryTimeGranularity = (
  days: number,
): ExpiryTimeGranularity => {

  const dayMs = expiryTimeGranularityMap().day;
  return getNearestExpiryTimeGranularity(days * dayMs);

};

export const TimeGranularityLabel = (timeGranularity: ExpiryTimeGranularity | undefined, translate: Translate) => {

  if (!timeGranularity) {

    return translate('general.text.none');

  }

  return translate(`general.text.time_granularity_types.${timeGranularity.type}`, { smart_count: timeGranularity.value });

};

export const convertTimeGranularityToDays = (timeGranularity: ExpiryTimeGranularity): number => {

  return (timeGranularity.value * expiryTimeGranularityMap(false)[timeGranularity.type]) / expiryTimeGranularityMap(false)[ExpiryTimeGranularityType.Day];

};

export const calculateLargestReminderPeriodInDays = (
  reminderPeriods: ExpiryTimeGranularity[],
): number => Math.max(0, ...(reminderPeriods ?? []).map(convertTimeGranularityToDays));

export const getMinimumNextDate = (startDate?: string): string => moment(startDate ?? undefined).startOf('day').add(1, 'day').format('YYYY-MM-DD');

export const calculateDateAfterTimeGranularity = (startDate: string | undefined, timeGranularity: ExpiryTimeGranularity | undefined) => {

  if (!startDate || !timeGranularity || !timeGranularity.type || timeGranularity.value === null) {

    return undefined;

  }

  return moment(startDate).add(timeGranularity.value, timeGranularity.type).format('YYYY-MM-DD');

};
